import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { db, auth } from '../../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { updateAvatar } from '../../redux/authSlice';

const STATUS_PRESETS = ['На работе', 'Отпуск', 'Командировка', 'Больничный'];

const SettingsModal = ({ user, onClose }) => {
  const dispatch = useDispatch();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [status, setStatus] = useState(user?.status || '');
  const [statusDays, setStatusDays] = useState(() => {
    if (user?.statusUntil && user.statusUntil > Date.now()) {
      return String(Math.ceil((user.statusUntil - Date.now()) / 86400000));
    }
    return '';
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1000000) {
      alert('Файл слишком большой! Выберите картинку до 1 МБ.');
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setDoc(doc(db, 'users', user.uid), { photoURL: base64String }, { merge: true })
        .then(() => {
          if (auth.currentUser) updateProfile(auth.currentUser, { photoURL: base64String });
          dispatch(updateAvatar(base64String));
          setUploading(false);
        })
        .catch((error) => {
          console.error('Ошибка сохранения фото:', error);
          setUploading(false);
        });
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const days = Number(statusDays);
      await setDoc(
        doc(db, 'users', user.uid),
        {
          displayName: displayName.trim(),
          status: status.trim() || 'На работе',
          // Если указаны дни — статус действует до этой даты
          statusUntil: days > 0 ? Date.now() + days * 24 * 60 * 60 * 1000 : null,
        },
        { merge: true }
      );
      onClose();
    } catch (error) {
      console.error('Ошибка сохранения настроек:', error);
      alert('Не удалось сохранить настройки.');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', display: 'flex',
        overflowY: 'auto', padding: '20px', boxSizing: 'border-box', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          margin: 'auto',
          background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(15px)', WebkitBackdropFilter: 'blur(15px)',
          border: '1px solid rgba(255,255,255,0.5)', borderRadius: '15px', padding: '20px',
          width: '100%', maxWidth: '420px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>⚙️ Настройки профиля</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#888' }}>✕</button>
        </div>

        {/* Аватар */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ position: 'relative' }}>
            <img
              src={user?.photoURL || 'https://via.placeholder.com/100'}
              alt="Avatar"
              style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #2a5298', background: '#eee' }}
            />
            {uploading && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.8)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>⏳</div>
            )}
          </div>
          <label style={{ display: 'inline-block', marginTop: '12px', padding: '8px 15px', background: '#f0f2f5', borderRadius: '20px', cursor: 'pointer', fontSize: '14px', border: '1px solid #ddd' }}>
            📸 Изменить фото
            <input type="file" accept="image/png, image/jpeg" onChange={handleAvatarUpload} style={{ display: 'none' }} disabled={uploading} />
          </label>
        </div>

        {/* Имя */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, fontSize: '14px', color: '#333' }}>Отображаемое имя</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Например: Иван"
            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', boxSizing: 'border-box' }}
          />
        </div>

        {/* Статус */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, fontSize: '14px', color: '#333' }}>Статус</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
            {STATUS_PRESETS.map((s) => (
              <button key={s} onClick={() => setStatus(s)}
                style={{
                  padding: '5px 10px', borderRadius: '14px', border: '1px solid #ddd', cursor: 'pointer', fontSize: '12px',
                  background: status === s ? '#2a5298' : '#fff', color: status === s ? '#fff' : '#333', fontWeight: 600,
                }}>
                {s}
              </button>
            ))}
          </div>
          <input
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Например: На работе, Отпуск, Командировка, Больничный"
            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', boxSizing: 'border-box' }}
          />
          <label style={{ display: 'block', margin: '8px 0 5px 0', fontSize: '12px', color: '#888' }}>
            На сколько дней (пусто — пока не поменяете сами). По истечении вернётся «На работе».
          </label>
          <input
            type="number"
            min="1"
            value={statusDays}
            onChange={(e) => setStatusDays(e.target.value)}
            placeholder="Например: 12"
            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', boxSizing: 'border-box' }}
          />
        </div>

        {/* Кнопки */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '12px', background: saving ? '#ccc' : '#2a5298', color: '#fff', border: 'none', borderRadius: '5px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700 }}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button onClick={onClose}
            style={{ flex: 1, padding: '12px', background: '#f0f2f5', color: '#333', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 700 }}>
            Отмена
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SettingsModal;